/**
 * Client Calendar Store
 * Manages calendar collection data for client users with company-specific filtering
 * Provides access to:
 * - Company's own events
 * - Other companies' events (shown as blocked/unavailable)
 * - Admin-created blocked dates
 */
import { defineStore } from "pinia";
import { ref, computed } from 'vue';
import {
  collection,
  Timestamp,
  onSnapshot
} from "firebase/firestore";
import { db, auth } from "../js/firebase";
import { logger } from '../services/loggerService';
import { calendarEventService } from '@/services/calendarEventService';
import { validateEventDuration, durationLimitsFor } from '@/utils/calendarEventTypes';

// Compute event duration in hours from date / dateEnd values that may be
// Date instances, Firestore Timestamps, or null/undefined. Returns null if
// either bound is missing so callers can skip duration-based validation.
function getDurationHours(date, dateEnd) {
  if (!date || !dateEnd) return null;
  const startMs = date instanceof Date ? date.getTime() : date.toMillis?.();
  const endMs = dateEnd instanceof Date ? dateEnd.getTime() : dateEnd.toMillis?.();
  if (typeof startMs !== 'number' || typeof endMs !== 'number') return null;
  return (endMs - startMs) / (1000 * 60 * 60);
}

// Firebase collection reference
const calendarCollectionRef = collection(db, "calendar");

export const useClientCalendarStore = defineStore('clientCalendar', () => {
  //-------------------------------
  // STATE
  //-------------------------------
  /**
   * Object containing all calendar events keyed by ID
   * Includes events from all companies for proper blocking/availability display
   * @type {Object}
   */
  const items = ref({});

  /**
   * Current company ID being viewed
   * @type {String}
   */
  const currentCompanyId = ref(null);

  /**
   * Loading state for async operations
   * @type {Boolean}
   */
  const loading = ref(false);

  /**
   * Whether the store has completed initial data load
   * @type {Boolean}
   */
  const initialized = ref(false);

  /**
   * Unsubscribe function for the real-time listener
   * @type {Function|null}
   */
  let unsubscribe = null;

  //-------------------------------
  // GETTERS
  //-------------------------------

  /**
   * Get all calendar events as an array
   */
  const allEvents = computed(() => Object.values(items.value));

  /**
   * Get events for the current company only
   */
  const companyEvents = computed(() => {
    if (!currentCompanyId.value) return [];
    return allEvents.value.filter(event => event.companyId === currentCompanyId.value);
  });

  /**
   * Get blocked dates (other companies' events and admin blocked dates)
   * Returns events that should be shown as unavailable to the client
   */
  const blockedDates = computed(() => {
    if (!currentCompanyId.value) return [];
    return allEvents.value.filter(event => {
      // Include events from other companies
      if (event.companyId && event.companyId !== currentCompanyId.value) {
        return true;
      }
      // Include admin-created blocked dates (no companyId or marked as blocked type)
      if (event.type === 'blocked' && (!event.companyId || event.isPublic)) {
        return true;
      }
      return false;
    });
  });

  /**
   * Get events for a specific project
   * @param {String} projectId
   */
  const getEventsByProject = (projectId) => {
    return allEvents.value.filter(event => event.projectId === projectId);
  };

  //-------------------------------
  // ACTIONS
  //-------------------------------

  /**
   * Initialize store and set up real-time listener for calendar events
   * Fetches ALL calendar events to properly show blocked dates from other companies
   * @param {String} companyId - The company ID to load events for
   */
  async function init(companyId) {
    if (!companyId) {
      logger.error('[ClientCalendar] Cannot initialize without companyId');
      return;
    }

    // Verify user is authenticated before setting up listener
    // This can temporarily be null during impersonation transitions - not a true error
    if (!auth.currentUser) {
      logger.debug('[ClientCalendar] Cannot initialize - no authenticated user (may be transient during impersonation)');
      return;
    }

    // If already initialized for this company with active listener, skip
    if (initialized.value && currentCompanyId.value === companyId && unsubscribe) {
      logger.debug('[ClientCalendar] Store already initialized for this company with active listener');
      return;
    }

    // Clean up any existing listener
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }

    loading.value = true;
    currentCompanyId.value = companyId;

    try {
      logger.info('[ClientCalendar] Initializing calendar store with real-time listener', { companyId });

      // Set up real-time listener for ALL calendar events
      // Client needs to see:
      // 1. Their own company's events
      // 2. Other companies' events (to know what dates are blocked)
      // 3. Admin-created blocked dates
      // Resolve on the first snapshot so callers (e.g. MsaOrderWizard seeding
      // existing bookings) don't read an empty store on a cold start.
      await new Promise((resolve) => {
        unsubscribe = onSnapshot(
          calendarCollectionRef,
          (snapshot) => {
            const eventsData = {};

            snapshot.forEach((doc) => {
              const data = doc.data();
              eventsData[doc.id] = {
                id: doc.id,
                ...data
                // Keep dates as Timestamps for proper handling
              };
            });

            items.value = eventsData;

            // Only log on first load
            if (!initialized.value) {
              logger.info('[ClientCalendar] Store initialized with real-time listener', {
                companyId,
                totalEvents: Object.keys(eventsData).length,
                companyEvents: companyEvents.value.length,
                blockedDates: blockedDates.value.length
              });
              initialized.value = true;
              loading.value = false;
            } else {
              logger.debug('[ClientCalendar] Real-time update received', {
                totalEvents: Object.keys(eventsData).length
              });
            }
            resolve();
          },
          (error) => {
            logger.firestoreListenerError('[ClientCalendar] Real-time listener error', error, { companyId });
            loading.value = false;

            // Handle permission-denied errors (typically from auth token expiration)
            if (error.code === 'permission-denied') {
              logger.warn('[ClientCalendar] Permission denied - cleaning up listener. User may need to re-authenticate.');
              // Clean up the failed listener
              if (unsubscribe) {
                unsubscribe();
                unsubscribe = null;
              }
              // Reset state so re-initialization can be attempted
              initialized.value = false;
            }
            resolve();
          }
        );
      });
    } catch (error) {
      logger.error('[ClientCalendar] Failed to initialize store:', {
        error: error.message,
        companyId
      });
      loading.value = false;
      throw error;
    }
  }
  
  /**
   * Fetch/refresh calendar events for a specific company
   * With real-time listener, this just ensures the listener is set up
   * @param {String} companyId - The company ID to fetch events for
   */
  async function fetchCalendarEvents(companyId) {
    if (!companyId) {
      logger.error('[ClientCalendar] Cannot fetch events without companyId');
      return;
    }

    // If company changed or no active listener, reinitialize
    if (currentCompanyId.value !== companyId || !unsubscribe) {
      await init(companyId);
    }

    logger.debug('[ClientCalendar] fetchCalendarEvents called - using real-time listener', {
      companyId,
      totalEvents: Object.keys(items.value).length
    });

    return items.value;
  }
  
  /**
   * Create a new calendar event
   * @param {Object} eventData - Event data to create
   */
  async function createEvent(eventData, options = {}) {
    try {
      logger.info('[ClientCalendar] Creating calendar event', { eventData });

      // Validate required fields
      if (!eventData.title || !eventData.companyId || !eventData.type || !eventData.date) {
        throw new Error('Missing required fields: title, companyId, type, and date are required');
      }

      // Enforce per-event-type duration limits (defense-in-depth — UI also
      // validates). MSA order bookings opt out: the studio 1–2h cap is a
      // self-serve calendar rule, not a contract-order constraint.
      if (!options.skipDurationLimit) {
        const durationHours = getDurationHours(eventData.date, eventData.dateEnd);
        if (durationHours !== null) {
          const typeValidation = validateEventDuration(eventData.type, durationHours, durationLimitsFor(eventData.type, eventData.showId));
          if (!typeValidation.valid) {
            throw new Error(typeValidation.error);
          }
        }
      }

      // Convert dates to Timestamps if needed
      const eventToAdd = {
        ...eventData,
        date: eventData.date instanceof Date
          ? Timestamp.fromDate(eventData.date)
          : eventData.date,
        dateEnd: eventData.dateEnd instanceof Date
          ? Timestamp.fromDate(eventData.dateEnd)
          : eventData.dateEnd || null,
        allDay: eventData.allDay !== undefined ? eventData.allDay : false
      };

      // Route through the service so activity logging fires with the correct
      // userType claim. The real-time listener will populate items.value.
      const result = await calendarEventService.createEvent(eventToAdd);

      logger.info('[ClientCalendar] Calendar event created', {
        id: result.id,
        title: eventData.title,
        companyId: eventData.companyId
      });

      return result.id;
    } catch (error) {
      logger.error('[ClientCalendar] Failed to create event:', {
        error: error.message,
        eventData
      });
      throw error;
    }
  }

  /**
   * Update an existing calendar event
   * @param {String} eventId - The event document ID
   * @param {Object} updates - Fields to update
   */
  async function updateEvent(eventId, updates, options = {}) {
    try {
      logger.info('[ClientCalendar] Updating calendar event', { eventId, updates });

      if (!eventId) {
        throw new Error('Event ID is required');
      }

      // Enforce per-event-type duration limits when an update touches the
      // time range. Resolve effective values (updates win, fall back to the
      // existing event in the local store). MSA order edits opt out.
      if (!options.skipDurationLimit && ('date' in updates || 'dateEnd' in updates || 'type' in updates)) {
        const existing = items.value[eventId] || {};
        const effectiveType = 'type' in updates ? updates.type : existing.type;
        const effectiveDate = 'date' in updates ? updates.date : existing.date;
        const effectiveEnd = 'dateEnd' in updates ? updates.dateEnd : existing.dateEnd;
        const effectiveShowId = 'showId' in updates ? updates.showId : existing.showId;
        const durationHours = getDurationHours(effectiveDate, effectiveEnd);
        if (durationHours !== null) {
          const typeValidation = validateEventDuration(effectiveType, durationHours, durationLimitsFor(effectiveType, effectiveShowId));
          if (!typeValidation.valid) {
            throw new Error(typeValidation.error);
          }
        }
      }

      // Convert dates to Timestamps if present
      const updatesToSave = { ...updates };
      if (updates.date instanceof Date) {
        updatesToSave.date = Timestamp.fromDate(updates.date);
      }
      if (updates.dateEnd instanceof Date) {
        updatesToSave.dateEnd = Timestamp.fromDate(updates.dateEnd);
      }

      logger.info('[ClientCalendar] Saving to Firestore', {
        eventId,
        updatesToSave,
        currentUserId: auth.currentUser?.uid
      });

      // Route through the service so activity logging captures the change with
      // the correct userType claim. The service stamps updatedAt/updatedBy and
      // writes one activity entry per changed field; the real-time listener
      // refreshes items.value. Passing showToast=false keeps existing UX where
      // the client UI manages its own success messaging.
      await calendarEventService.updateEvent(eventId, updatesToSave, false);

      logger.info('[ClientCalendar] Calendar event updated', { eventId });

      return true;
    } catch (error) {
      logger.error('[ClientCalendar] Failed to update event:', {
        error: error.message,
        eventId,
        updates
      });
      throw error;
    }
  }

  /**
   * Delete a calendar event
   * @param {String} eventId - The event document ID
   */
  async function deleteEvent(eventId) {
    try {
      logger.info('[ClientCalendar] Deleting calendar event', { eventId });

      if (!eventId) {
        throw new Error('Event ID is required');
      }

      // Route through the service so the activity + productionSchedules
      // subcollections are cleaned up alongside the event. The real-time
      // listener removes the entry from items.value.
      await calendarEventService.deleteEvent(eventId);

      logger.info('[ClientCalendar] Calendar event deleted', { eventId });

      return true;
    } catch (error) {
      logger.error('[ClientCalendar] Failed to delete event:', {
        error: error.message,
        eventId
      });
      throw error;
    }
  }
  
  /**
   * Clear all cached data, reset initialization, and clean up listener
   */
  function reset() {
    // Clean up real-time listener
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
    items.value = {};
    currentCompanyId.value = null;
    loading.value = false;
    initialized.value = false;
    logger.info('[ClientCalendar] Store reset');
  }

  //-------------------------------
  // RETURN PUBLIC INTERFACE
  //-------------------------------
  return {
    // State
    items,
    currentCompanyId,
    loading,
    initialized,

    // Getters
    allEvents,
    companyEvents,
    blockedDates,
    getEventsByProject,

    // Actions
    init,
    fetchCalendarEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    reset
  };
});